import React from 'react';

import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { Screen } from '@confluence/screen';
import {
	VIEW_PAGE,
	VIEW_BLOG_DATE_LEGACY,
	VIEW_BLOG,
} from '@confluence/named-routes/entry-points/routes.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { ViewPageLiteLoader } from '@confluence/view-page/entry-points/ViewPageLite.ts';
import { ViewPageLiteHeaderLoader } from '@confluence/view-page/entry-points/ViewPageLiteHeader.ts';

import SendTrackViewPageEvent from '../../SendTrackViewPageEvent/index.js';
import { ContentExtensionsProvider } from '../../ContentExtensionsProvider/index.ts';
import SpaceBase from '../../SpaceBase/index.js';
import { LazyDwellTime, LazyConsumptionViewedAndCompletedEventTracker } from '../lazyComponents.js';

export const ViewPageLiteRoute = ({
	name: routeName,
	params: { contentId, spaceKey, contentSlug },
}: RouteMatch): React.JSX.Element => {
	const isPage = routeName === VIEW_PAGE.name;
	const isBlog = routeName === VIEW_BLOG_DATE_LEGACY.name || routeName === VIEW_BLOG.name;
	let contentType;

	// any one of VIEW_BLOG_DATE_LEGACY, VIEW_BLOG or VIEW_PAGE route can end up here from ViewPageOrBlogRoute
	if (isBlog) {
		contentType = 'blogpost';
	} else if (isPage) {
		contentType = 'page';
	}

	return (
		<Screen
			showPremiumOnboarding={false}
			screenEvent={{
				name: 'viewPageScreen',
				id: contentId,
				attributes: {
					isPublicView: false,
					pageLoadInfo: getUniquePageLoadId(),
				},
				objectType: contentType,
				objectId: contentId,
			}}
			pageState={{
				contentId,
				spaceKey,
				routeName,
				contentSlug,
				isLite: true,
				contentType,
			}}
		>
			<ViewPageLiteHeaderLoader />
			<SpaceBase>
				<ContentExtensionsProvider>
					<ViewPageLiteLoader spaceKey={spaceKey} contentId={contentId}>
						<ErrorBoundary attribution={Attribution.BACKBONE}>
							<SendTrackViewPageEvent pageType={contentType} />
						</ErrorBoundary>
					</ViewPageLiteLoader>
				</ContentExtensionsProvider>
			</SpaceBase>
			<LazyDwellTime />
			<LazyConsumptionViewedAndCompletedEventTracker
				spaceKey={spaceKey}
				source="ViewPageLiteRoute"
			/>
		</Screen>
	);
};
